import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { motion } from 'framer-motion';
import logoStandard from '../images/logo-standard.png';
import logoDark from '../images/logo-dark.png';

export default function Header({ onNavigateGallery }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    setIsMenuOpen(false);
  };

  const handleNavClick = (item) => {
    if (item.id === 'trending-deals' && onNavigateGallery) {
      onNavigateGallery();
    } else {
      scrollToSection(item.id);
    }
  };

  const menuItems = [
    { label: 'Book tid', id: 'booking' },
  ];

  return (
    <header className={`w-full px-4 sm:px-6 md:px-10 lg:px-20 py-3 sm:py-4 sticky top-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/20 backdrop-blur-md shadow-sm' : 'bg-Neautral-white shadow-sm'
    }`}>
      {/* Mobile Layout - Logo left, Hamburger right */}
      <div className="flex md:hidden justify-between items-center w-full">
        <motion.img
          src={logoStandard}
          alt="Studio Søraa Logo"
          className="h-10 sm:h-12 cursor-pointer"
          onClick={() => scrollToSection('hero')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        />

        {/* Hamburger Button */}
        <div className="text-Neautral-main cursor-pointer w-6 h-6 flex items-center justify-center">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="absolute"
          >
            {isMenuOpen ? (
              <X size={24} strokeWidth={2} />
            ) : (
              <Menu size={24} strokeWidth={2} />
            )}
          </button>
        </div>
      </div>

      {/* Desktop Layout - Centered Logo and Nav */}
      <div className="hidden md:flex flex-row items-center justify-center w-full gap-8 lg:gap-12">
        <motion.img
          src={logoStandard}
          alt="Studio Søraa Logo"
          className="h-12 md:h-14 cursor-pointer"
          onClick={() => scrollToSection('hero')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        />

        {/* Navigation Links - Desktop */}
        <nav className="flex justify-center items-center gap-6 lg:gap-12">
          {menuItems.map((item, index) => (
            <motion.div
              key={item.id}
              className="text-Neautral-main text-sm md:text-base font-semibold font-opensans tracking-tight cursor-pointer group relative w-max"
              onClick={() => handleNavClick(item)}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.3 }}
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 w-0 transition-all h-0.5 bg-Neautral-main group-hover:w-full"></span>
            </motion.div>
          ))}
        </nav>
      </div>

      {/* Mobile Navigation */}
      <nav
        className={`fixed inset-0 top-0 left-0 w-full h-screen bg-Pink-BG backdrop-blur-md md:hidden z-40 flex flex-col items-center justify-center gap-8 transition-all duration-500 ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        {/* Close Button - Positioned at top right */}
        {isMenuOpen && (
          <button
            onClick={() => setIsMenuOpen(false)}
            className="absolute top-5 text-Neautral-main"
            style={{ right: '10px' }}
          >
            <X size={32} strokeWidth={2} />
          </button>
        )}

        {/* Menu Items */}
        <div className="flex flex-col justify-center items-center gap-8">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item)}
              className="text-Neautral-main text-2xl font-semibold font-opensans tracking-tight cursor-pointer group relative w-max"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 w-0 transition-all h-0.5 bg-Neautral-main group-hover:w-full"></span>
            </button>
          ))}
        </div>

        {/* Logo - Centered at bottom */}
        <div className="absolute bottom-8 flex justify-center items-center">
          <motion.img
            src={logoDark}
            alt="Studio Søraa Logo"
            className="h-12 sm:h-16"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection('hero')}
          />
        </div>
      </nav>
    </header>
  );
}
